import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { getDatabase, onValue, ref } from "firebase/database";
import { useAuthState } from "react-firebase-hooks/auth";
import { app } from "../firebase";
import { fetchMyStreams, pickSelected, selectedStreamKey, StreamInfo } from "../utils/streams";

interface StreamsState {
  /** True until the account's streams (and its profile) have been read. */
  loading: boolean;
  streams: StreamInfo[];
  selected: StreamInfo | null;
  /** The stream the account was created with (the one on its profile). */
  primaryName: string | null;
  select: (name: string) => void;
  /** Reads the streams again, for example after one was added or renamed. */
  reload: () => Promise<void>;
}

const StreamsContext = createContext<StreamsState>({
  loading: true, streams: [], selected: null, primaryName: null, select: () => undefined, reload: async () => undefined,
});

const readSaved = (uid: string): string | null => {
  try { return localStorage.getItem(selectedStreamKey(uid)); } catch { return null; }
};

// How long to wait for the profile before carrying on without it. It is only needed when the API has no streams for the account.
const PROFILE_WAIT_MS = 4000;

/**
 * The signed-in account's streams and which one every page is showing. The list comes from the API; the stream on the account's
 * profile is the fallback when the API cannot be reached, and is the default selection.
 */
export const StreamsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user] = useAuthState(getAuth(app));
  const uid = user?.uid ?? null;
  const [apiStreams, setApiStreams] = useState<StreamInfo[] | null>(null); // null = not answered yet
  const [apiFailed, setApiFailed] = useState(false);
  const [profile, setProfile] = useState<StreamInfo | null | undefined>(undefined); // undefined = not read yet
  const [saved, setSaved] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user) return;
    try {
      setApiStreams(await fetchMyStreams(() => user.getIdToken()));
      setApiFailed(false);
    } catch (error) {
      console.error("Could not load the account's streams:", error);
      setApiFailed(true);
      setApiStreams([]);
    }
  }, [user]);

  useEffect(() => {
    setApiStreams(null);
    setApiFailed(false);
    setProfile(undefined);
    setSaved(uid ? readSaved(uid) : null);
    if (!uid) return;
    const unsubscribe = onValue(
      ref(getDatabase(), `users/${uid}`),
      (snapshot) => {
        const data = snapshot.val();
        setProfile(data?.name ? { name: data.name, imageUrl: data.imageLink || "", color: data.color || "" } : null);
      },
      (error) => { console.error("Firebase read failed: ", error); setProfile(null); }
    );
    // A profile read that never answers (offline, blocked) must not hold every page on its loading screen.
    const giveUp = setTimeout(() => setProfile((current) => (current === undefined ? null : current)), PROFILE_WAIT_MS);
    reload();
    return () => { unsubscribe(); clearTimeout(giveUp); };
  }, [uid, reload]);

  const select = useCallback((name: string) => {
    setSaved(name);
    if (!uid) return;
    try { localStorage.setItem(selectedStreamKey(uid), name); } catch { /* storage blocked: the choice lasts until reload */ }
  }, [uid]);

  const value = useMemo<StreamsState>(() => {
    // The API's list is the truth, even when empty: a stream handed to another account stays on this profile, and must not reappear.
    // The profile is only a stand-in while the API cannot be reached.
    const loading = !!uid && (apiStreams === null || (apiFailed && apiStreams.length === 0 && profile === undefined));
    const streams = apiStreams && apiStreams.length > 0 ? apiStreams : apiFailed && profile ? [profile] : [];
    return { loading, streams, selected: pickSelected(streams, saved, profile?.name ?? null), primaryName: profile?.name ?? null, select, reload };
  }, [uid, apiStreams, apiFailed, profile, saved, select, reload]);

  return <StreamsContext.Provider value={value}>{children}</StreamsContext.Provider>;
};

export const useStreams = () => useContext(StreamsContext);

/**
 * Re-creates its children whenever the selected stream changes, so a page never shows one stream's questions, contacts or
 * history while loading another's, and its polling and state start clean.
 */
export const ByStream: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { selected } = useStreams();
  return <React.Fragment key={selected?.name ?? ""}>{children}</React.Fragment>;
};
